import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import iotvConfig from '@iotv/config';
import { AdjacentType, AlertIncidentType, BranchingQueryDefinition, camelCase_to_Sentence, getSpecificEdgeTypeId, Incident, IncidentStatus, IncidentTypeNames, NetworkApplicationServerClassName, NodeEdgeMapper, Severity, SYSTEM, toTitleCase, } from '@iotv/datamodel';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Autocomplete, Box, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import config from '../../../config';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { getNiceDate } from '../../../data/TypeHelpers';
import { isAdminRole } from '../../../helpers/access';
import { ExpressionFunctionType, LogicalOperatorType } from '../../../types';
import { DataGridRadioColumn } from '../../../util/DataGrid/DataGridHelpers';
import { AutosizedTextField } from '../../common/AutosizedTextField';
import { SingleEntityAutoQuery } from '../../generic/EntityAutoQuery';
import { IncidentView } from '../Incidents/IncidentView';
const debug = process.env.REACT_APP_DEBUG && true;
const dynamo = config.aws.dynamoDB;
const incidentProducingTypes = iotvConfig.incidentProducingTypes ?? [];
const columnLayout = {
    display: 'flex',
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
};
const incidentColumns = [
    {
        field: 'status',
        headerName: 'Status',
        headerAlign: 'center',
        ...columnLayout,
        width: 65,
        display: 'flex',
        align: 'center',
        type: 'custom',
        renderCell(params) {
            let StatusIcon = undefined;
            switch (params.value) {
                case IncidentStatus.OPEN:
                    StatusIcon = _jsx(WarningIcon, { color: "warning" });
                    break;
                case IncidentStatus.CLOSED:
                    StatusIcon = _jsx(CheckIcon, { color: "success" });
                    break;
            }
            return StatusIcon;
        },
    },
    {
        field: 'severity',
        headerName: 'Severity',
        headerAlign: 'center',
        ...columnLayout,
        minWidth: 150,
        display: 'flex',
        align: 'center',
        type: 'custom',
        renderCell(params) {
            // handle typo in enum (now fixed)
            const value = params.value === 'MEDUIM' ? 'MEDIUM' : params.value;
            const severity = (value ?? Severity.NONE);
            const severities = Object.keys(Severity).reverse();
            const index = severities.findIndex((s) => s === severity);
            const level = 0 <= index ? index : 0;
            const icons = [];
            for (let i = 0; i < level; i++) {
                icons.push(_jsx(WarningIcon, { color: params.row.status === IncidentStatus.CLOSED ? 'success' : 'warning' }));
            }
            for (let i = level; i < severities.length - 1; i++) {
                icons.push(_jsx(WarningAmberIcon, { sx: { opacity: 0.6 } }));
            }
            return _jsxs(Stack, { direction: "row", children: [...icons] });
        },
    },
    {
        field: 'incidentType',
        headerName: 'Type',
        minWidth: 160,
        ...columnLayout,
        valueFormatter: (value) => (value ? IncidentTypeNames[value] : value),
    },
    {
        field: 'timestamp',
        headerName: 'Opened',
        ...columnLayout,
        minWidth: 160,
        valueFormatter: (value) => (value && getNiceDate(value)) ?? value,
    },
    {
        field: 'lastUpdated',
        headerName: 'Updated',
        ...columnLayout,
        minWidth: 160,
        valueFormatter: (value) => (value && getNiceDate(value)) ?? value,
    },
    {
        field: 'closedTimestamp',
        headerName: 'Closed',
        ...columnLayout,
        minWidth: 160,
        valueFormatter: (value) => (value && getNiceDate(value)) ?? value,
    },
    {
        field: 'summary',
        headerName: 'Summary',
        ...columnLayout,
        flex: 1,
    },
];
const columns = [DataGridRadioColumn, ...incidentColumns];
export const IncidentsTab = ({ contextCustomer, user, userGroupRoles, userSelectedGroup, roleArbitrationInstance, userFunctions, transactionFunctions, isSmallView, }) => {
    const isAdmin = isAdminRole(userSelectedGroup);
    const [selectedUser, setSelectedUser] = useState(null);
    const [seedCustomer, setSeedCustomer] = useState();
    const isSeedCustomer = seedCustomer && contextCustomer ? seedCustomer.sk === contextCustomer.sk : undefined;
    const [queryFilterState, setQueryFilterState] = useState(IncidentStatus.OPEN);
    const [queryFilterIncidentType, setQueryFilterIncidentType] = useState();
    const handleQueryModeStateChange = (value) => setQueryFilterState(value);
    const handleQueryModeIncidentTypeChange = (value) => setQueryFilterIncidentType(value);
    // data
    const [loading, setLoading] = useState(false);
    const [incidents, setIncidents] = useState([]);
    const [selectedIncident, setSelectedIncident] = useState(undefined);
    useEffect(() => {
        setLoading(true);
        ConsumerDao.getNamedApexItems({ type: 'Customer' }).then(({ err, data }) => {
            if (err)
                debug && console.log('IncidentsTab', 'getNamedApexItems', err);
            if (data && data.length)
                setSeedCustomer(data[0]);
            else
                setSeedCustomer(undefined);
            setLoading(false);
        });
    }, [contextCustomer?.sk]);
    // row selection
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    useEffect(() => {
        const selectedIds = 'ids' in rowSelectionModel ? Array.from(rowSelectionModel.ids) : rowSelectionModel;
        setSelectedIncident(incidents.find((n) => n.id === selectedIds[0]));
    }, [rowSelectionModel, incidents]);
    /**
     * Before touching anything, please bear in mind the level of complexity involved that the business requirements imply
     *
     * I think it makes most sense to work backwards, from the incidents
     *
     * - Incidents are pseudo-nodes (edge-only) attached to the entity that triggered them
     * - The entities are usually VOBs that the user cares about
     * - These VOBs are in groups that the user has been shared (made visible)
     *
     * Thus, for a regular user, we query from them, to their user group, to their shared groups (which support the incident-producing types we care about), and then to the entities, from which we can query the TSI index for time-sorted (most-recent first) incidents
     *
     * If the user is an admin user for that customer, we follow a different path: from context customer, to groups (which support the incident-producing types we care about), to the entities, and from there to incidents (as above)
     *
     * If the user has access to the seed customer, we add additional queries for: system incidents, attached to the system object, and backend incidents, attached to Lambda functions, and network incidents, attached to LoRaWAN NAS's
     */
    const fetch = async () => {
        setLoading(true);
        const getCustomerIncidentsForType = async (objectTypeId) => await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextCustomer],
            query: new BranchingQueryDefinition({
                id: objectTypeId,
                path: [
                    {
                        adjacencyType: AdjacentType.CHILD,
                        objectTypeId: 'Group',
                        nodeFilters: [
                            { setOperation: LogicalOperatorType.AND, filters: [{ key: 'supportedType', predicate: ExpressionFunctionType.CONTAINS, value: objectTypeId }] },
                        ],
                    },
                    { adjacencyType: AdjacentType.CHILD, objectTypeId },
                ],
                subQueries: [],
            }),
        });
        const getUserIncidentsForType = async (objectTypeId) => await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [isAdmin ? (selectedUser ?? user) : user],
            query: new BranchingQueryDefinition({
                id: objectTypeId,
                path: [
                    {
                        adjacencyType: AdjacentType.PARENT,
                        objectTypeId: 'Group',
                    },
                    {
                        adjacencyType: AdjacentType.CHILD,
                        objectTypeId: 'Group',
                        edgeTypeId: getSpecificEdgeTypeId('Group', 'Group', 'group_allows_group'),
                        nodeFilters: [
                            { setOperation: LogicalOperatorType.AND, filters: [{ key: 'supportedType', predicate: ExpressionFunctionType.CONTAINS, value: objectTypeId }] },
                        ],
                    },
                    { adjacencyType: AdjacentType.CHILD, objectTypeId },
                ],
                subQueries: [],
            }),
        });
        const getSystemIncidentsForType = async (objectTypeId) => await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [SYSTEM],
            query: new BranchingQueryDefinition({
                id: objectTypeId,
                path: [{ adjacencyType: AdjacentType.CHILD, objectTypeId }],
                subQueries: [],
            }),
        });
        const getForAdmin = isAdmin && selectedUser === null;
        const promises = incidentProducingTypes.map(getForAdmin ? getCustomerIncidentsForType : getUserIncidentsForType);
        if (isSeedCustomer && isAdmin) {
            // INFO: LoRa NAS's are special-cased
            promises.unshift(getSystemIncidentsForType(NetworkApplicationServerClassName));
            // INFO: Lambda's are special-cased
            promises.unshift(getSystemIncidentsForType('LambdaFunction'));
        }
        const entityNEMs = await Promise.all(promises);
        const entities = entityNEMs.reduce((a, c) => {
            if (c.err)
                throw new Error(c.err.message);
            if (c.data)
                a.union(c.data);
            return a;
        }, new NodeEdgeMapper());
        const getIncidentsForType = (objectTypeId) => entities.getNodesOfType(objectTypeId);
        const vobs = incidentProducingTypes.map(getIncidentsForType).reduce((a, c) => a.concat(c), []);
        if (isSeedCustomer && isAdmin) {
            // INFO: LoRa NAS's are special-cased
            vobs.unshift(...getIncidentsForType(NetworkApplicationServerClassName));
            // INFO: Lambda's are special-cased
            vobs.unshift(...getIncidentsForType('LambdaFunction'));
            // INFO: the system object is special-cased
            vobs.unshift(SYSTEM);
        }
        /** TODO: handle `ExclusiveStartKey` for continued querying? */
        const getIncidentsForVOB = (vob) => {
            return ConsumerDao.baseQuery({
                TableName: dynamo.tableName,
                IndexName: dynamo.tsiName,
                ScanIndexForward: false,
                ExpressionAttributeNames: {
                    '#pk': dynamo.pKey,
                    '#sk': dynamo.sKey,
                    '#summary': '_summary',
                    '#status': '_status',
                    '#severity': '_severity',
                    '#incidentType': '_incidentType',
                    '#timestamp': 'timestamp',
                    '#lastUpdated': '_lastUpdated',
                    '#closedTimestamp': 'closedTimestamp',
                },
                ExpressionAttributeValues: {
                    ':pk': vob.sk,
                    ':type': 'Incident:',
                },
                KeyConditionExpression: '#pk = :pk',
                FilterExpression: 'begins_with(#sk, :type)',
                ProjectionExpression: 'sk,#summary,#status,#severity,#incidentType,#timestamp,#lastUpdated,#closedTimestamp',
                Limit: 50,
                ExclusiveStartKey: undefined,
            });
        };
        const incidentRes = await Promise.all(vobs.map(getIncidentsForVOB));
        const accumulated = incidentRes.reduce((a, c) => {
            if (c.err)
                throw new Error(c.err.message);
            if (c.data?.Items)
                a = a.concat(c.data.Items);
            return a;
        }, []);
        const instantiated = accumulated.map((incident) => new Incident({ ...incident, id: incident.sk }));
        const sortedIncidents = instantiated.toSorted((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
        setIncidents(sortedIncidents);
        setLoading(false);
    };
    useEffect(() => {
        if (typeof isSeedCustomer === 'boolean' && (isAdmin ? contextCustomer : (selectedUser ?? user)))
            fetch();
    }, [isSeedCustomer, isAdmin, contextCustomer?.sk, selectedUser?.sk, user?.sk, userSelectedGroup]);
    let filteredIncidents = incidents;
    if (queryFilterState)
        filteredIncidents = filteredIncidents.filter((incident) => incident.status === queryFilterState);
    if (queryFilterIncidentType)
        filteredIncidents = filteredIncidents.filter((incident) => incident.incidentType === queryFilterIncidentType);
    return (_jsxs(Stack, { minHeight: "40cqh", marginBlockStart: isAdmin ? 1 : queryFilterState ? 1 : 0, gap: 1, sx: { transition: 'margin-block-start 0.15s ease-in-out' }, children: [_jsx(Stack, { direction: "row", children: _jsxs(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: 'repeat(1, auto)', md: 'repeat(2, auto)' }, gap: 1, alignItems: 'center' }, children: [isAdmin && (_jsxs(_Fragment, { children: [_jsx(Typography, { children: "View incidents for" }), _jsx(SingleEntityAutoQuery, { contextObject: contextCustomer, entityType: "User", currentEntity: selectedUser, setCurrentEntity: (u) => setSelectedUser(u), label: "User", fullWidth: false, clearable: true })] })), _jsx(Typography, { children: "Filter by status" }), _jsx(Autocomplete, { freeSolo: false, size: "small", fullWidth: true, multiple: false, value: queryFilterState ?? null, options: Object.values(IncidentStatus), getOptionLabel: (option) => toTitleCase(camelCase_to_Sentence(option)), onChange: (_, value) => handleQueryModeStateChange(value), renderInput: (inputProps) => _jsx(AutosizedTextField, { ...inputProps, label: 'Incident status' }) }), _jsx(Typography, { children: "Filter by type" }), _jsx(Autocomplete, { freeSolo: false, size: "small", fullWidth: true, multiple: false, value: queryFilterIncidentType ?? null, options: Object.values(AlertIncidentType), getOptionLabel: (option) => IncidentTypeNames[option], onChange: (_, value) => handleQueryModeIncidentTypeChange(value), renderInput: (inputProps) => _jsx(AutosizedTextField, { ...inputProps, label: 'Incident type' }) })] }) }), incidentProducingTypes.length === 0 && _jsx(Alert, { severity: "warning", children: "No incident-producing types are configured!" }), _jsx(DataGrid, { loading: loading, rows: loading ? [] : filteredIncidents, columns: columns, checkboxSelection: true, pageSizeOptions: [10], initialState: { pagination: { paginationModel: { pageSize: 10 } } }, hideFooter: filteredIncidents.length <= 10, hideFooterPagination: filteredIncidents.length <= 10, hideFooterSelectedRowCount: true, disableMultipleRowSelection: true, rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: setRowSelectionModel }), selectedIncident && (_jsx(IncidentView, { contextCustomer: contextCustomer, user: user, userGroupRoles: userGroupRoles, userSelectedGroup: userSelectedGroup, roleArbitrationInstance: roleArbitrationInstance, userFunctions: userFunctions, transactionFunctions: transactionFunctions, isSmallView: isSmallView, incidentSk: selectedIncident.sk }, selectedIncident.sk))] }, "IncidentsTabMainGrid"));
};
